import MenuItem, { type MenuItemProps } from '@/components/DropdownMenu/Item'
import { ActionIcon, Popover } from '@mantine/core'
import { IconDownload } from '@tabler/icons-react'
import clsx from 'clsx'
import { type MouseEvent, useState } from 'react'
import toast from 'react-hot-toast'
import type { ShareImageTarget } from './types'
import useShareImageResource from './useShareImageResource'
import styles from './ShareImageMenuItem.module.css'
import ShareImageMenuPreview from './ShareImageMenuPreview'

interface ShareImageMenuItemProps extends MenuItemProps {
	target: ShareImageTarget
}

export default function ShareImageMenuItem({
	target,
	onClick,
	className,
	...props
}: ShareImageMenuItemProps) {
	const [opened, setOpened] = useState(false)
	const [copying, setCopying] = useState(false)
	const [saving, setSaving] = useState(false)
	const { prepare, preview } = useShareImageResource(target)

	const handlePreview = () => {
		setOpened(true)
		void prepare().catch(() => {})
	}
	const handleCopy = async (
		_id: string,
		_data: MenuItemProps['data'],
		event: MouseEvent<HTMLElement>
	) => {
		event.stopPropagation()
		if (copying) return
		if (typeof ClipboardItem === 'undefined' || !navigator.clipboard?.write) {
			toast.error('此浏览器不支持复制图片，请点击下载图标保存')
			return
		}
		setCopying(true)
		const message = toast.loading('正在复制分享图…')
		try {
			// Register the promise during the click, preserving Safari's user activation.
			await navigator.clipboard.write([
				new ClipboardItem({ 'image/png': prepare().then(({ blob }) => blob) }),
			])
			toast.success('已复制分享图', { id: message })
			onClick?.(props.data.id, props.data, event)
		} catch {
			toast.error('复制失败，请重试或下载保存', { id: message })
		} finally {
			setCopying(false)
		}
	}
	const handleSave = async (event: MouseEvent<HTMLAnchorElement>) => {
		event.stopPropagation()
		if (preview.url) return
		event.preventDefault()
		if (saving) return
		setSaving(true)
		try {
			const { url } = await prepare()
			const link = document.createElement('a')
			link.href = url
			link.download = `jike-${target.kind}-${target.data.id}.png`
			link.click()
		} catch {
			toast.error('分享图保存失败，请重试')
		} finally {
			setSaving(false)
		}
	}

	return (
		<Popover
			opened={opened}
			position="right"
			offset={8}
			closeOnClickOutside={false}
			closeOnEscape={false}
			styles={{
				dropdown: {
					pointerEvents: 'none',
					padding: 0,
					border: 0,
					borderRadius: 0,
					boxShadow: 'none',
					background: 'transparent',
				},
			}}
		>
			<Popover.Target>
				<div
					className={styles.row}
					onMouseEnter={handlePreview}
					onMouseLeave={() => setOpened(false)}
					onFocus={handlePreview}
					onBlur={(event) => {
						if (!event.currentTarget.contains(event.relatedTarget)) setOpened(false)
					}}
				>
					<MenuItem
						{...props}
						className={clsx(className, styles.copy)}
						disabled={copying}
						tabIndex={0}
						onClick={handleCopy}
					/>
					<ActionIcon
						component="a"
						href={preview.url}
						download={`jike-${target.kind}-${target.data.id}.png`}
						tabIndex={0}
						className={styles.download}
						aria-label="保存分享图"
						title="保存图片"
						variant="subtle"
						color="gray"
						size={22}
						loading={saving}
						onClick={handleSave}
					>
						<IconDownload size={16} />
					</ActionIcon>
				</div>
			</Popover.Target>
			<Popover.Dropdown>
				<ShareImageMenuPreview preview={preview} />
			</Popover.Dropdown>
		</Popover>
	)
}
